import {
  RpmConfigV2,
  RpmConfigV2Schema,
  RpmTrackerCardConfigV1,
  RpmTrackerCardTypeV1,
  RpmTrackerChartV1,
  RpmTrackerConfigV2,
  RpmTrackerParameterConfigV1,
  RpmTrackerParameterDataConfigV1,
  RpmV2EmailNotificationJobConfig,
  RpmV2MonitoringEmailNotificationJobConfig,
} from '@csp/config-schemas';
import { EnumUtil } from '@csp/csp-common-enum-util';
import {
  EmailJobConfig,
  EmailJobCronPattern,
  EmailNotificationJobConfig,
  KafkaEmailJobConfig,
  Maybe,
  MonitoringEmailNotificationJobConfig,
  RpmConfig,
  RpmTrackerCardConfig,
  RpmTrackerCardType,
  RpmTrackerChartConfig,
  RpmTrackerConfig,
  RpmTrackerParameterConfig,
  RpmTrackerParameterDataConfig,
} from '@csp/csp-common-model';
import { CommonHcpWebPath } from '@csp/csp-common-routes';
import { JsonValidationService, isDefined } from '@csp/csp-common-util';
import { RoleType } from '@csp/dmdp-api-common-dto';
import { SymptomMonitoringEmailJobConfig } from '../../email-job/model/SymptomMonitoringEmailJobConfig';

type ScheduleRequestRef = {
  scheduleCode: string;
  requestCode: string;
};

const toParameterDataConfig = (
  parameterDataConfigV1: RpmTrackerParameterDataConfigV1,
  defaultScheduleRequestRef?: ScheduleRequestRef,
): Maybe<RpmTrackerParameterDataConfig> => {
  switch (parameterDataConfigV1.type) {
    case 'QUESTIONNAIRE_DATA':
      if (parameterDataConfigV1.dataQuery && parameterDataConfigV1.valueQuery) {
        return {
          type: parameterDataConfigV1.type,
          dataQuery: {
            ...parameterDataConfigV1.dataQuery,
            scheduleRequestRef: parameterDataConfigV1.dataQuery.scheduleRequestRef ?? defaultScheduleRequestRef,
          },
          valueQuery: parameterDataConfigV1.valueQuery,
        };
      }
      break;
    case 'DEVICE_DATA':
      if (parameterDataConfigV1.dataQuery) {
        return {
          type: parameterDataConfigV1.type,
          dataQuery: {
            ...parameterDataConfigV1.dataQuery,
            scheduleRequestRef: parameterDataConfigV1.dataQuery.scheduleRequestRef ?? defaultScheduleRequestRef,
          },
          valueQuery: parameterDataConfigV1.valueQuery,
        };
      }
      break;
    case 'INTERNAL_CHARTS':
      if (parameterDataConfigV1.chartDataRefs) {
        return {
          type: parameterDataConfigV1.type,
          chartDataRefs: parameterDataConfigV1.chartDataRefs,
        };
      }
      break;
  }
  return undefined;
};

const toParameterConfig = (
  parameterConfigV1: RpmTrackerParameterConfigV1,
  defaultScheduleRequestRef?: ScheduleRequestRef,
): Maybe<RpmTrackerParameterConfig> => {
  const dataConfig = toParameterDataConfig(parameterConfigV1.dataConfig, defaultScheduleRequestRef);
  return dataConfig
    ? {
        parameterCode: parameterConfigV1.parameterCode,
        type: parameterConfigV1.type,
        dataConfig,
      }
    : undefined;
};

const toChartConfig = (
  chartV1: RpmTrackerChartV1,
  defaultScheduleRequestRef?: ScheduleRequestRef,
): RpmTrackerChartConfig => ({
  chartCode: chartV1.chartCode,
  type: chartV1.chartConfig.type,
  parameters: chartV1.chartConfig.parameters
    .map(param => toParameterConfig(param, chartV1.defaultScheduleRequestRef ?? defaultScheduleRequestRef))
    .filter(isDefined),
});

const toCardType = (type: RpmTrackerCardTypeV1): RpmTrackerCardType => {
  switch (type) {
    case 'DEFAULT':
      return 'OVERVIEW';
    default:
      return type;
  }
};

const toCardConfig = (cardConfigV1: RpmTrackerCardConfigV1): RpmTrackerCardConfig => ({
  cardCode: cardConfigV1.cardCode,
  type: toCardType(cardConfigV1.type),
  charts: cardConfigV1.charts.map(chart => toChartConfig(chart, cardConfigV1.defaultScheduleRequestRef)),
});

const toTrackerConfig = (trackerConfigV2: Maybe<RpmTrackerConfigV2>): Maybe<RpmTrackerConfig> => {
  if (trackerConfigV2) {
    return {
      daysToTrack: trackerConfigV2.daysToTrack,
      externalReportUrl: trackerConfigV2.externalReportUrl,
      enableStudyDays: trackerConfigV2.enableStudyDays ?? false,
      cards: trackerConfigV2.cards.map(toCardConfig),
    };
  } else {
    return undefined;
  }
};

const toPatientColumnLink = (patientColumnLink: Maybe<string>): Maybe<CommonHcpWebPath> =>
  EnumUtil.fromMaybeString<CommonHcpWebPath>(patientColumnLink, CommonHcpWebPath);

const toEmailNotificationJobConfig = (
  emailNotificationJobConfig: RpmV2EmailNotificationJobConfig,
): EmailNotificationJobConfig => ({
  daily: emailNotificationJobConfig.daily && {
    ...emailNotificationJobConfig.daily,
    patientColumnLink: toPatientColumnLink(emailNotificationJobConfig.daily?.patientColumnLink),
  },
  weekly: emailNotificationJobConfig.weekly && {
    ...emailNotificationJobConfig.weekly,
    patientColumnLink: toPatientColumnLink(emailNotificationJobConfig.weekly?.patientColumnLink),
  },
  monthly: emailNotificationJobConfig.monthly && {
    ...emailNotificationJobConfig.monthly,
    patientColumnLink: toPatientColumnLink(emailNotificationJobConfig.monthly?.patientColumnLink),
  },
  single: emailNotificationJobConfig.single && {
    ...emailNotificationJobConfig.single,
    patientColumnLink: toPatientColumnLink(emailNotificationJobConfig.single?.patientColumnLink),
  },
});

const toMonitoringEmailNotificationJobConfig = (
  emailNotificationJobConfig: RpmV2EmailNotificationJobConfig | RpmV2MonitoringEmailNotificationJobConfig,
): MonitoringEmailNotificationJobConfig => ({
  daily: emailNotificationJobConfig.daily && {
    ...emailNotificationJobConfig.daily,
    patientColumnLink: toPatientColumnLink(emailNotificationJobConfig.daily?.patientColumnLink),
  },
  weekly: emailNotificationJobConfig.weekly && {
    ...emailNotificationJobConfig.weekly,
    patientColumnLink: toPatientColumnLink(emailNotificationJobConfig.weekly?.patientColumnLink),
  },
  monthly: emailNotificationJobConfig.monthly && {
    ...emailNotificationJobConfig.monthly,
    patientColumnLink: toPatientColumnLink(emailNotificationJobConfig.monthly?.patientColumnLink),
  },
});

const toRpmConfig = (rpmConfigV2: RpmConfigV2): RpmConfig => {
  JsonValidationService.validateJsonNonStrict('RpmConfigV2.json', RpmConfigV2Schema, rpmConfigV2);

  return {
    modules: rpmConfigV2.patientRemoteMonitoring.modules.map(module => ({
      moduleCode: module.moduleCode,
      trackerConfig: toTrackerConfig(module.trackerConfig),
    })),
    formulaCodes: rpmConfigV2.formulaCodes,
    emailNotificationJobConfig:
      rpmConfigV2.emailNotificationJobConfig && toEmailNotificationJobConfig(rpmConfigV2.emailNotificationJobConfig),
    monitoringEmailNotificationJobConfig:
      rpmConfigV2.monitoringEmailNotificationJobConfig &&
      toMonitoringEmailNotificationJobConfig(rpmConfigV2.monitoringEmailNotificationJobConfig),
  };
};

const toRoleTypes = (roles: string[]): RoleType[] =>
  roles.map(roleAsString => EnumUtil.fromString<RoleType>(roleAsString, RoleType)).filter(isDefined);

const getRpmSingleSymptomEmail = (rpmConfigV2: RpmConfigV2): Maybe<KafkaEmailJobConfig> => {
  const deprecatedSingleSymptomConfig = rpmConfigV2.emailNotificationJobConfig?.single;
  if (deprecatedSingleSymptomConfig) {
    return deprecatedSingleSymptomConfig.enabled
      ? {
          roles: toRoleTypes(deprecatedSingleSymptomConfig.roles),
          patientColumnLink: EnumUtil.fromMaybeString<CommonHcpWebPath>(
            deprecatedSingleSymptomConfig.patientColumnLink,
            CommonHcpWebPath,
          ),
        }
      : undefined;
  }

  const singleSymptomConfig = rpmConfigV2.notifications?.singleSymptomEvent;
  return (
    singleSymptomConfig && {
      roles: toRoleTypes(singleSymptomConfig.roles),
      patientColumnLink: EnumUtil.fromMaybeString<CommonHcpWebPath>(
        singleSymptomConfig.patientColumnLink,
        CommonHcpWebPath,
      ),
    }
  );
};

const getRpmSummaryEmails = (rpmConfigV2: RpmConfigV2): EmailJobConfig[] => {
  const deprecatedSummaryEmails = rpmConfigV2.emailNotificationJobConfig;
  if (deprecatedSummaryEmails?.daily || deprecatedSummaryEmails?.weekly || deprecatedSummaryEmails?.monthly) {
    const { daily, weekly, monthly } = deprecatedSummaryEmails;

    return [
      daily?.enabled
        ? EmailJobConfig.from({
            code: 'DAILY',
            cronPattern: EmailJobCronPattern.DAILY,
            roles: toRoleTypes(daily.roles),
            patientColumnLink: daily.patientColumnLink,
            groupBy: undefined,
          })
        : undefined,
      weekly?.enabled
        ? EmailJobConfig.from({
            code: 'WEEKLY',
            cronPattern: EmailJobCronPattern.WEEKLY,
            roles: toRoleTypes(weekly.roles),
            patientColumnLink: weekly.patientColumnLink,
            groupBy: undefined,
          })
        : undefined,
      monthly?.enabled
        ? EmailJobConfig.from({
            code: 'MONTHLY',
            cronPattern: EmailJobCronPattern.MONTHLY,
            roles: toRoleTypes(monthly.roles),
            patientColumnLink: monthly.patientColumnLink,
            groupBy: undefined,
          })
        : undefined,
    ].filter(isDefined);
  }

  const summaryEmails = rpmConfigV2.notifications?.eventSummary?.emails ?? [];
  return summaryEmails.map(({ code, cronPattern, roles, patientColumnLink, groupBy }) =>
    EmailJobConfig.from({ code, cronPattern, roles: toRoleTypes(roles), patientColumnLink, groupBy }),
  );
};

const getRpmMonitoringEmails = (rpmConfigV2: RpmConfigV2): SymptomMonitoringEmailJobConfig[] => {
  const deprecatedMonitoringEmails = rpmConfigV2.monitoringEmailNotificationJobConfig;
  if (deprecatedMonitoringEmails?.daily || deprecatedMonitoringEmails?.weekly || deprecatedMonitoringEmails?.monthly) {
    const { daily, weekly, monthly } = deprecatedMonitoringEmails;

    return [
      daily?.enabled
        ? SymptomMonitoringEmailJobConfig.from({
            code: 'DAILY',
            cronPattern: EmailJobCronPattern.DAILY,
            roles: toRoleTypes(daily.roles),
            patientColumnLink: daily.patientColumnLink,
            groupBy: undefined,
            includeReviewStatus: undefined,
          })
        : undefined,
      weekly?.enabled
        ? SymptomMonitoringEmailJobConfig.from({
            code: 'WEEKLY',
            cronPattern: EmailJobCronPattern.WEEKLY,
            roles: toRoleTypes(weekly.roles),
            patientColumnLink: weekly.patientColumnLink,
            groupBy: undefined,
            includeReviewStatus: undefined,
          })
        : undefined,
      monthly?.enabled
        ? SymptomMonitoringEmailJobConfig.from({
            code: 'MONTHLY',
            cronPattern: EmailJobCronPattern.MONTHLY,
            roles: toRoleTypes(monthly.roles),
            patientColumnLink: monthly.patientColumnLink,
            groupBy: undefined,
            includeReviewStatus: undefined,
          })
        : undefined,
    ].filter(isDefined);
  }

  const monitoringEmails = rpmConfigV2.notifications?.symptomMonitoring?.emails ?? [];
  return monitoringEmails.map(({ code, cronPattern, roles, patientColumnLink, groupBy, includeReviewStatus }) =>
    SymptomMonitoringEmailJobConfig.from({
      code,
      cronPattern,
      roles: toRoleTypes(roles),
      patientColumnLink,
      groupBy,
      includeReviewStatus,
    }),
  );
};

const toEmailConfig = (
  rpmConfigV2: RpmConfigV2,
): {
  rpmSummaryEmails: EmailJobConfig[];
  rpmMonitoringEmails: SymptomMonitoringEmailJobConfig[];
  rpmSingleSymptomEmail: Maybe<KafkaEmailJobConfig>;
} => ({
  rpmSummaryEmails: getRpmSummaryEmails(rpmConfigV2),
  rpmMonitoringEmails: getRpmMonitoringEmails(rpmConfigV2),
  rpmSingleSymptomEmail: getRpmSingleSymptomEmail(rpmConfigV2),
});

export const RpmConfigService = {
  toRpmConfig,
  toEmailConfig,
};
